import styled from 'styled-components';
import { motion } from 'framer-motion';

export const ModalContainer = styled.div`
  width: 100%;
  max-width: 1600px;
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  margin: 0 auto;
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 80vh;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  @media (max-width: 1400px) {
    max-width: 95vw;
    margin: 0 8px;
    min-height: 80vh;
  }

  @media (max-width: 600px) {
    max-width: 98vw;
    min-height: 90vh;
  }
`;

export const ModalHeader = styled.div`
  padding: 16px 24px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const ModalTitleContainer = styled.div`
  flex: 1;
`;

export const ModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
`;

export const ModalDescription = styled.p`
  font-size: 0.875rem;
  color: #64748b;
  margin: 4px 0 0 0;
`;

export const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  color: #64748b;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f1f5f9;
    color: #1e293b;
  }
`;

export const ModalContent = styled.div`
  padding: 24px;
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;

  @media (max-width: 600px) {
    padding: 16px;
  }
`;

export const ModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 16px 24px;
  border-top: 1px solid #e2e8f0;

  @media (max-width: 600px) {
    padding: 12px 16px;
  }
`;

export const StepperContainer = styled.div`
  margin-bottom: 24px;
`;

export const StepperWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

export const StepItem = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: 600px) {
    width: 100%;
  }
`;

export const StepNumber = styled.div<{ active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${(props) =>
    props.active ? '#293778' : '#e2e8f0'};
  color: ${(props) => (props.active ? 'white' : '#64748b')};
  font-weight: 500;
  margin-right: 8px;
`;

export const StepLabel = styled.div`
  font-weight: 500;
  color: #1e293b;
`;

export const StepDivider = styled.div`
  flex: 1;
  height: 2px;
  background-color: #e2e8f0;
  margin: 0 8px;

  @media (max-width: 600px) {
    display: none;
  }
`;

export const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background-color: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
`;

export const ProgressFill = styled.div<{ progress: number }>`
  height: 100%;
  background-color: #293778;
  border-radius: 4px;
  transition: width 0.3s ease;
  width: ${(props) => `${props.progress}%`};
`;

export const FormGroup = styled.div`
  margin-bottom: 16px;
`;

export const FormLabel = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #1e293b;
  margin-bottom: 6px;
`;

export const FormInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  font-size: 0.875rem;
  color: #1e293b;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #293778;
    box-shadow: 0 0 0 2px rgba(41, 55, 120, 0.1);
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

export const FormSelect = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  font-size: 0.875rem;
  color: #1e293b;
  background-color: white;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #293778;
    box-shadow: 0 0 0 2px rgba(41, 55, 120, 0.1);
  }
`;

export const FormTextarea = styled.textarea`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  font-size: 0.875rem;
  color: #1e293b;
  transition: border-color 0.2s ease;
  min-height: 80px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #293778;
    box-shadow: 0 0 0 2px rgba(41, 55, 120, 0.1);
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

export const FormError = styled.div`
  color: #dc2626;
  font-size: 0.75rem;
  margin-top: 4px;
`;

export const TabsContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

export const TabsList = styled.div`
  display: flex;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 16px;
`;

export const TabButton = styled.button<{ active: boolean }>`
  padding: 8px 16px;
  background: none;
  border: none;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${(props) => (props.active ? '#293778' : '#64748b')};
  cursor: pointer;
  border-bottom: 2px solid
    ${(props) => (props.active ? '#293778' : 'transparent')};
  transition: all 0.2s ease;

  &:hover {
    color: ${(props) => (props.active ? '#293778' : '#1e293b')};
  }
`;

export const SourcesContainer = styled.div`
  margin-bottom: 16px;
`;

export const SourcesStepContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

export const SourcesList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-right: 8px;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: #e2e8f0;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #293778;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #1e2654;
  }
`;

export const SourceItem = styled.div<{ selected: boolean }>`
  padding: 16px;
  border: 1px solid
    ${(props) => (props.selected ? '#293778' : '#e2e8f0')};
  border-radius: 6px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.1s ease;
  background-color: ${(props) =>
    props.selected ? 'rgba(41, 55, 120, 0.05)' : 'white'};

  &:hover {
    background-color: ${(props) =>
      props.selected ? 'rgba(41, 55, 120, 0.05)' : '#f8fafc'};
  }
`;

export const SourceItemContent = styled.div`
  display: flex;
  align-items: center;
`;

export const SourceItemText = styled.div`
  margin-left: 8px;
  font-weight: 500;
  color: #1e293b;
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 960px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const FormSection = styled.div`
  padding: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  margin-bottom: 16px;
`;

export const FormSectionTitle = styled.h4`
  font-size: 0.875rem;
  font-weight: 500;
  color: #1e293b;
  margin: 0 0 12px 0;
`;

export const SearchContainer = styled.div`
  position: relative;
  margin-bottom: 16px;
`;

export const SearchInput = styled.input`
  width: 100%;
  padding: 8px 12px 8px 36px;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  font-size: 0.875rem;
  color: #1e293b;

  &:focus {
    outline: none;
    border-color: #293778;
    box-shadow: 0 0 0 2px rgba(41, 55, 120, 0.1);
  }
`;

export const SearchIconWrapper = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  display: flex;
  align-items: center;
`;

export const SectionHeading = styled.h3`
  font-size: 1.125rem;
  font-weight: 500;
  color: #1e293b;
  margin: 0 0 16px 0;
`;

export const StickyHeader = styled.div`
  position: sticky;
  top: 0;
  background-color: white;
  z-index: 10;
`;

export const SectionDescription = styled.p`
  font-size: 0.875rem;
  color: #64748b;
  margin: 0 0 16px 0;
`;

export const StepContent = styled(motion.div)`
  width: 100%;
  flex: 1;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
`;

export const StyledBadge = styled.div<{ backgroundColor: string }>`
  height: 20px;
  min-width: 80px;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ backgroundColor }) =>
    backgroundColor || '#9E9E9E'};
  padding: 0 8px;

  & > span {
    font-size: 0.75rem;
    font-weight: 500;
    color: white;
    line-height: 20px;
    text-align: center;
    white-space: nowrap;
  }
`;

export const StyledWarning = styled.div`
  display: flex;
  align-items: center;
  background-color: rgb(250, 242, 230);
  color: rgb(234, 132, 77);
  border: 1px solid rgb(254, 186, 84);
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 0.8rem;
  line-height: 1.2;
  height: 30px;
  margin: 0 16px;
  flex: 1;
  max-width: 50%;
  gap: 6px;
`;
